import React, { useState, useEffect } from 'react';
import { 
  FiX, 
  FiUser, 
  FiHome, 
  FiCheck, 
  FiAlertCircle, 
  FiUsers, 
  FiArrowRight,
  FiSearch,
  FiFilter
} from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../../firebase';
import './FranchiseAssignmentModal.css';

const FranchiseAssignmentModal = ({ 
  isOpen, 
  onClose, 
  operators = [], 
  franchises = [], 
  onAssignmentUpdate 
}) => {
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFranchise, setFilterFranchise] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: Select Operator, 2: Select Franchise, 3: Confirm

  // Filter operators based on search and franchise
  const filteredOperators = operators.filter(operator => {
    const matchesSearch = !searchTerm || 
      operator.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      operator.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFranchise = filterFranchise === 'all' || 
      filterFranchise === 'unassigned' ? !operator.franchiseId : operator.franchiseId === filterFranchise;
    
    return matchesSearch && matchesFranchise && operator.globalRole === 'operator';
  });

  // Get unassigned operators
  const unassignedOperators = operators.filter(op => 
    op.globalRole === 'operator' && !op.franchiseId
  );

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedOperator(null);
      setSelectedFranchise(null);
      setSearchTerm('');
      setFilterFranchise('all');
      setStep(1);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleOperatorSelect = (operator) => {
    setSelectedOperator(operator);
    setStep(2);
  };

  const handleFranchiseSelect = (franchise) => {
    setSelectedFranchise(franchise);
    setStep(3);
  };

  const handleAssignOperator = async () => {
    if (!selectedOperator || !selectedFranchise) {
      setError('Please select both an operator and franchise');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userOperations = httpsCallable(functions, 'userOperations');
      const result = await userOperations({
        operation: 'assignOperatorToFranchise',
        operatorId: selectedOperator.id,
        franchiseId: selectedFranchise.id
      });

      if (result.data.success) {
        setSuccess(`Successfully assigned ${selectedOperator.displayName || selectedOperator.email} to ${selectedFranchise.name}`);
        onAssignmentUpdate?.();
        
        // Auto-close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.data.error?.message || 'Failed to assign operator');
      }
    } catch (err) {
      console.error('Error assigning operator:', err);
      setError('Failed to assign operator: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOperator = async () => {
    if (!selectedOperator?.franchiseId) {
      setError('Operator is not assigned to any franchise');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userOperations = httpsCallable(functions, 'userOperations');
      const result = await userOperations({
        operation: 'removeOperatorFromFranchise',
        operatorId: selectedOperator.id,
        franchiseId: selectedOperator.franchiseId
      });

      if (result.data.success) {
        setSuccess(`Successfully removed ${selectedOperator.displayName || selectedOperator.email} from franchise`);
        onAssignmentUpdate?.();
        
        // Auto-close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.data.error?.message || 'Failed to remove operator');
      }
    } catch (err) {
      console.error('Error removing operator:', err);
      setError('Failed to remove operator: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    }
  };

  const getFranchiseName = (franchiseId) => {
    const franchise = franchises.find(f => f.id === franchiseId);
    return franchise?.name || 'Unknown Franchise';
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
        <div className="step-number">1</div>
        <span className="step-label">Select Operator</span>
      </div>
      <div className="step-connector" />
      <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
        <div className="step-number">2</div>
        <span className="step-label">Select Franchise</span>
      </div>
      <div className="step-connector" />
      <div className={`step ${step >= 3 ? 'active' : ''}`}>
        <div className="step-number">3</div>
        <span className="step-label">Confirm</span>
      </div>
    </div>
  );

  const renderOperatorSelection = () => (
    <div className="selection-step">
      <div className="step-header">
        <h3>Select Operator</h3>
        <p>Choose an operator to assign to a franchise</p>
      </div>

      <div className="search-and-filter">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search operators..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <FiFilter className="filter-icon" />
          <select
            value={filterFranchise}
            onChange={(e) => setFilterFranchise(e.target.value)}
          >
            <option value="all">All Operators</option>
            <option value="unassigned">Unassigned Only</option>
            {franchises.map(franchise => (
              <option key={franchise.id} value={franchise.id}>
                {franchise.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="operator-list">
        {filteredOperators.length === 0 ? (
          <div className="empty-state">
            <FiUsers className="empty-icon" />
            <h4>No operators found</h4>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          filteredOperators.map(operator => (
            <div
              key={operator.id}
              className={`operator-card ${selectedOperator?.id === operator.id ? 'selected' : ''}`}
              onClick={() => handleOperatorSelect(operator)}
            >
              <div className="operator-info">
                <div className="operator-avatar">
                  <FiUser />
                </div>
                <div className="operator-details">
                  <h4>{operator.displayName || 'No Name'}</h4>
                  <p className="operator-email">{operator.email}</p>
                  <div className="operator-status">
                    {operator.franchiseId ? (
                      <span className="status-assigned">
                        Assigned to {getFranchiseName(operator.franchiseId)}
                      </span>
                    ) : (
                      <span className="status-unassigned">
                        Unassigned
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="operator-actions">
                <FiArrowRight className="select-icon" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderFranchiseSelection = () => (
    <div className="selection-step">
      <div className="step-header">
        <h3>Select Franchise</h3>
        <p>Choose a franchise for {selectedOperator?.displayName || selectedOperator?.email}</p>
      </div>

      <div className="franchise-list">
        {franchises.map(franchise => (
          <div
            key={franchise.id}
            className={`franchise-card ${selectedFranchise?.id === franchise.id ? 'selected' : ''}`}
            onClick={() => handleFranchiseSelect(franchise)}
          >
            <div className="franchise-info">
              <div className="franchise-avatar">
                <FiHome />
              </div>
              <div className="franchise-details">
                <h4>{franchise.name}</h4>
                <p className="franchise-location">{franchise.location || 'No location specified'}</p>
                <div className="franchise-stats">
                  <span className="stat">
                    {franchise.operatorCount || 0} operators
                  </span>
                  <span className="stat">
                    {franchise.blockCount || 0} blocks
                  </span>
                </div>
              </div>
            </div>
            <div className="franchise-actions">
              <FiArrowRight className="select-icon" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderConfirmation = () => (
    <div className="confirmation-step">
      <div className="step-header">
        <h3>Confirm Assignment</h3>
        <p>Please review the assignment details</p>
      </div>

      <div className="assignment-summary">
        <div className="summary-card">
          <div className="summary-section">
            <h4>Operator</h4>
            <div className="summary-item">
              <div className="summary-avatar">
                <FiUser />
              </div>
              <div className="summary-details">
                <p className="summary-name">{selectedOperator?.displayName || 'No Name'}</p>
                <p className="summary-email">{selectedOperator?.email}</p>
              </div>
            </div>
          </div>

          <div className="summary-arrow">
            <FiArrowRight />
          </div>

          <div className="summary-section">
            <h4>Franchise</h4>
            <div className="summary-item">
              <div className="summary-avatar">
                <FiHome />
              </div>
              <div className="summary-details">
                <p className="summary-name">{selectedFranchise?.name}</p>
                <p className="summary-location">{selectedFranchise?.location || 'No location'}</p>
              </div>
            </div>
          </div>
        </div>

        {selectedOperator?.franchiseId && (
          <div className="assignment-note">
            <FiAlertCircle className="note-icon" />
            <p>
              This operator is currently assigned to {getFranchiseName(selectedOperator.franchiseId)}. 
              Assigning to a new franchise will remove them from their current franchise.
            </p>
          </div>
        )}
      </div>

      <div className="confirmation-actions">
        <button
          className="btn-secondary"
          onClick={handleBack}
          disabled={loading}
        >
          Back
        </button>
        
        {selectedOperator?.franchiseId && (
          <button
            className="btn-warning"
            onClick={handleRemoveOperator}
            disabled={loading}
          >
            {loading ? 'Removing...' : 'Remove from Current Franchise'}
          </button>
        )}
        
        <button
          className="btn-primary"
          onClick={handleAssignOperator}
          disabled={loading}
        >
          {loading ? 'Assigning...' : 'Confirm Assignment'}
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay franchise-assignment-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Franchise Assignment</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {renderStepIndicator()}

        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              <FiAlertCircle />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <FiCheck />
              <span>{success}</span>
            </div>
          )}

          {step === 1 && renderOperatorSelection()}
          {step === 2 && renderFranchiseSelection()}
          {step === 3 && renderConfirmation()}
        </div>

        <div className="modal-footer">
          <div className="footer-stats">
            <span className="stat">
              {unassignedOperators.length} unassigned operators
            </span>
            <span className="stat">
              {franchises.length} franchises available
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseAssignmentModal; 